import { useEffect, useRef, useCallback } from 'react';

interface UseDynamicFontSizeOptions {
  minFontSize?: number;
  maxFontSize?: number;
  step?: number;
  containerPadding?: number;
  maxHeight?: number;
}

export const useDynamicFontSize = <T extends HTMLElement = HTMLElement>(options: UseDynamicFontSizeOptions = {}) => {
  const {
    minFontSize = 0.8,
    maxFontSize = 2.2,
    step = 0.1,
    containerPadding = 40,
    maxHeight = 80
  } = options;

  const elementRef = useRef<T | null>(null);

  const adjustFontSize = useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    const container = element.parentElement;
    if (!container) return;

    const containerWidth = container.offsetWidth - containerPadding;
    const containerHeight = maxHeight;

    // Start with the maximum font size
    let fontSize = maxFontSize;
    element.style.fontSize = `${fontSize}rem`;

    // Reduce font size until text fits
    while (
      (element.scrollWidth > containerWidth || element.scrollHeight > containerHeight) && 
      fontSize > minFontSize
    ) {
      fontSize -= step;
      element.style.fontSize = `${fontSize}rem`;
    }
  }, [minFontSize, maxFontSize, step, containerPadding, maxHeight]);

  useEffect(() => {
    // Adjust on mount
    adjustFontSize();

    // Adjust on window resize
    const handleResize = () => {
      adjustFontSize();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adjustFontSize]);

  return elementRef;
}; 